import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle, ArrowRight, ArrowLeft, Loader2, Shield } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function SuccessMessage({ reference }) {
  return (
    <Card className="border-border shadow-xl max-w-2xl mx-auto">
      <CardContent className="p-8 text-center">
        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="font-heading text-2xl font-bold text-foreground mb-4">
          Thank You for Your Submission
        </h3>
        <p className="text-muted-foreground mb-6">
          Your claim enquiry has been received. Our team at Blue Lion Law will review your 
          information and contact you to discuss the next steps.
        </p>
        <p className="text-sm text-muted-foreground mb-6">
          Reference: <span className="font-mono font-semibold">{reference}</span>
        </p>
        <div className="p-4 rounded-lg bg-muted/50 border border-border">
          <p className="text-sm text-muted-foreground">
            <Shield className="w-4 h-4 inline mr-2" />
            Please note: Eligibility for compensation depends on individual circumstances.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

function QuestionStep({ question, answers, onAnswer }) {
  return (
    <div>
      <h3 className="font-heading text-xl font-bold text-foreground mb-6">
        {question.question}
      </h3>
      <div className="grid gap-3">
        {question.options.map(function(option, idx) {
          const isSelected = answers[question.id] === option.value;
          return (
            <button
              key={idx}
              onClick={function() { onAnswer(question.id, option.value); }}
              className={isSelected 
                ? 'w-full p-4 rounded-xl border-2 text-left border-accent bg-accent/5'
                : 'w-full p-4 rounded-xl border-2 text-left border-border hover:border-accent/50'
              }
            >
              <span className="font-medium text-foreground">{option.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ContactForm({ onSubmit, isSubmitting, error }) {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [consent, setConsent] = useState(false);
  const [marketingOptOut, setMarketingOptOut] = useState(false);

  function handleSubmit(e) {
    e.preventDefault();
    onSubmit({ fullName, email, phone, consent, marketingOptOut });
  }

  return (
    <form onSubmit={handleSubmit}>
      <h3 className="font-heading text-xl font-bold text-foreground mb-2">
        Your Contact Details
      </h3>
      <p className="text-muted-foreground mb-6">
        Please provide your details so we can get in touch to discuss your potential claim.
      </p>

      <div className="space-y-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">Full Name *</label>
          <Input
            type="text"
            value={fullName}
            onChange={function(e) { setFullName(e.target.value); }}
            placeholder="Enter your full name"
            required
            data-testid="claim-full-name"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">Email Address *</label>
          <Input
            type="email"
            value={email}
            onChange={function(e) { setEmail(e.target.value); }}
            placeholder="Enter your email"
            required
            data-testid="claim-email"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">Phone Number *</label>
          <Input
            type="tel"
            value={phone}
            onChange={function(e) { setPhone(e.target.value); }}
            placeholder="Enter your phone number"
            required
            data-testid="claim-phone"
          />
        </div>
      </div>

      {/* Data Processing Consent */}
      <div className="p-4 rounded-lg bg-muted/50 border border-border mb-4">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={consent}
            onChange={function(e) { setConsent(e.target.checked); }}
            className="mt-1 w-5 h-5"
            data-testid="claim-consent-checkbox"
          />
          <span className="text-sm text-muted-foreground">
            I consent to Blue Lion Law Limited processing my personal data for the purpose of 
            assessing my potential claim and I agree to the{' '}
            <a href="/privacy" className="text-accent underline hover:text-accent/80" target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </a>. *
          </span>
        </label>
      </div>

      {/* Marketing Opt-Out */}
      <div className="p-4 rounded-lg bg-muted/30 border border-border mb-6">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={marketingOptOut}
            onChange={function(e) { setMarketingOptOut(e.target.checked); }}
            className="mt-1 w-5 h-5"
            data-testid="claim-marketing-optout-checkbox"
          />
          <span className="text-sm text-muted-foreground italic">
            In accordance with our Privacy Policy, we may send you electronic marketing about similar 
            products or services which you have enquired about or asked us to provide, if you do not 
            want to receive this, please tick here.
          </span>
        </label>
      </div>

      {error && (
        <div className="p-4 rounded-lg bg-red-50 border border-red-200 mb-6">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      <Button type="submit" variant="gold" size="lg" className="w-full" disabled={isSubmitting} data-testid="claim-submit-btn">
        {isSubmitting ? 'Submitting...' : 'Submit My Enquiry'}
      </Button>

      <p className="text-xs text-muted-foreground text-center mt-4">
        Submitting this form does not guarantee a claim.
      </p>
    </form>
  );
}

function ClaimForm({ claimType, questions }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [reference, setReference] = useState('');
  const [error, setError] = useState('');

  const totalSteps = questions.length + 1;
  const showContactForm = currentStep >= questions.length;

  function handleAnswer(questionId, answer) {
    const newAnswers = Object.assign({}, answers);
    newAnswers[questionId] = answer;
    setAnswers(newAnswers);
    setTimeout(function() { setCurrentStep(currentStep + 1); }, 300);
  }

  async function handleContactSubmit(contactInfo) {
    setError('');

    if (!contactInfo.consent) {
      setError('Please confirm you consent to us processing your information.');
      return;
    }

    if (!contactInfo.fullName || !contactInfo.email || !contactInfo.phone) {
      setError('Please fill in all contact details.');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(BACKEND_URL + '/api/claims/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          claim_type: claimType,
          full_name: contactInfo.fullName,
          email: contactInfo.email,
          phone: contactInfo.phone,
          answers: answers,
          consent_given: contactInfo.consent,
          marketing_opt_out: contactInfo.marketingOptOut
        })
      });

      const data = await response.json();

      if (response.ok) {
        setIsSubmitted(true);
        setReference(data.reference);
      } else {
        setError(data.detail || 'Failed to submit. Please try again.');
      }
    } catch (err) {
      setError('Failed to submit. Please try again or call us on 0208 153 0033.');
    }

    setIsSubmitting(false);
  }

  if (isSubmitted) {
    return <SuccessMessage reference={reference} />;
  }

  const currentQuestion = questions[currentStep];

  return (
    <Card className="border-border shadow-xl max-w-2xl mx-auto">
      <CardContent className="p-8">
        <div className="mb-8">
          <div className="flex justify-between text-sm text-muted-foreground mb-2">
            <span>Step {currentStep + 1} of {totalSteps}</span>
            <span>{Math.round(((currentStep + 1) / totalSteps) * 100)}% Complete</span>
          </div>
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div 
              className="h-full bg-accent transition-all duration-500"
              style={{ width: ((currentStep + 1) / totalSteps * 100) + '%' }}
            />
          </div>
        </div>

        {showContactForm ? (
          <ContactForm 
            onSubmit={handleContactSubmit} 
            isSubmitting={isSubmitting} 
            error={error} 
          />
        ) : (
          <QuestionStep 
            question={currentQuestion} 
            answers={answers} 
            onAnswer={handleAnswer} 
          />
        )}

        {currentStep > 0 && (
          <button
            onClick={function() { setCurrentStep(currentStep - 1); }}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mt-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to previous question
          </button>
        )}
      </CardContent>
    </Card>
  );
}

export { ClaimForm };
